/**
 * Which meal it is, from the clock.
 *
 * "Special occasion? / Just dinner" was wrong twice over at half past eight in the
 * morning: the label named the wrong meal, and the search that followed made no use of
 * the one thing the app already knew for certain, which is what time it is.
 *
 * The meal is read at question-build time rather than inside an `apply`, because every
 * `apply` is a pure function of `Facts` and reaching for the clock inside one would make
 * a run unreproducible. What the "Eating right now?" answer decides is whether to *use*
 * the meal, not what it is.
 *
 * Deliberately not a place type. Table A has `breakfast_restaurant` and
 * `brunch_restaurant` but nothing for lunch or dinner, and setting `includedType` from
 * here would collide with whatever the food trees chose - the same reason the dietary
 * filter steers the text query instead. "Somewhere for lunch" is answered by a good
 * Italian place that serves lunch, not only by a dedicated lunch spot.
 */
export type Meal = 'breakfast' | 'brunch' | 'lunch' | 'dinner';

/**
 * Boundaries are local-time hours, and deliberately coarse: this steers relevance
 * ranking rather than filtering, so being an hour out costs a slightly worse sort and
 * nothing else. Late night folds into dinner - at 1am "dinner" is the right search, and
 * "still open" is already handled by `openNow`.
 */
export function mealAt(date: Date): Meal {
  const hour = date.getHours();
  const day = date.getDay();
  const weekend = day === 0 || day === 6;

  if (hour >= 17 || hour < 5) return 'dinner';
  // Brunch is a weekend thing. On a Tuesday at ten it is still breakfast.
  if (weekend && hour >= 9 && hour < 14) return 'brunch';
  if (hour < 11) return 'breakfast';
  return 'lunch';
}

/**
 * How the meal reads in a search phrase.
 *
 * Breakfast covers brunch as well: someone eating at nine wants either, and the two are
 * separate Table A types, so a phrase is the only way to ask for both at once.
 */
const MEAL_QUERY: Record<Meal, string> = {
  breakfast: 'breakfast or brunch',
  brunch: 'brunch',
  lunch: 'lunch',
  dinner: 'dinner',
};

export function mealQuery(meal: Meal): string {
  return MEAL_QUERY[meal];
}

/** The everyday half of "Special occasion?", named for the meal actually in front of you. */
export function mealCasualLabel(meal: Meal): string {
  return `Just ${meal}`;
}

/**
 * The two meals that are their own kind of place.
 *
 * Breakfast and brunch spots are a category in a way lunch and dinner are not - people
 * go *for brunch* rather than for brunch-flavoured Italian - and Table A agrees, with a
 * type for each and nothing for the other two. So at those hours it is worth asking
 * outright before sending anyone down the cuisine trees, and taking the answer as the
 * whole food decision when it comes back yes.
 */
const MEAL_PLACE_TYPES: Partial<Record<Meal, string>> = {
  breakfast: 'breakfast_restaurant',
  brunch: 'brunch_restaurant',
};

/** The Table A type for a meal that is its own kind of place, if it is one. */
export function mealPlaceType(meal: Meal): string | undefined {
  return MEAL_PLACE_TYPES[meal];
}

/** Every type the meal shortcut can set. Pinned alongside the trees by the tests. */
export function mealPlaceTypes(): string[] {
  return Object.values(MEAL_PLACE_TYPES);
}

/**
 * Whether the run already asked for the meal as a place, in which case the search phrase
 * must not say it twice - "breakfast for breakfast or brunch" is not a query.
 */
export function isMealPlaceType(type: string | undefined): boolean {
  return type !== undefined && mealPlaceTypes().includes(type);
}
